<template>
  <v-container fluid>
    <!-- Center search -->
    <v-card class="pa-3 mb-3">
      <div class="title mb-2">{{ $t("agent.searchTitle") }}</div>
      <v-layout row wrap align-center>
        <v-flex xs12 sm8 class="px-2">
          <v-text-field
            v-model="search"
            :label="$t('agent.searchHint')"
            append-icon="search"
            :loading="searching"
            @keyup.enter="doSearch"
            @click:append="doSearch"
          />
        </v-flex>
        <v-flex xs12 sm4 class="px-2">
          <v-btn color="primary" block :loading="searching" @click="doSearch">
            {{ $t("agent.search") }}
          </v-btn>
        </v-flex>
      </v-layout>
      <v-list two-line v-if="centers.length">
        <v-list-tile
          v-for="c in centers"
          :key="c.id"
          @click="selectCenter(c)"
          :class="{ 'grey lighten-3': selected && selected.center && selected.center.id === c.id }"
        >
          <v-list-tile-avatar>
            <v-icon :color="c.disabled ? 'grey' : c.low_balance ? 'red' : 'green'">store</v-icon>
          </v-list-tile-avatar>
          <v-list-tile-content>
            <v-list-tile-title>
              #{{ c.id }} — {{ c.name }}
              <v-chip v-if="c.low_balance" color="red" text-color="white" small>{{ $t("agent.lowBalance") }}</v-chip>
              <v-chip v-if="c.disabled" color="grey" text-color="white" small>{{ $t("agent.disabled") }}</v-chip>
            </v-list-tile-title>
            <v-list-tile-sub-title>
              {{ c.phone_number }} · {{ c.address }} · {{ $t("agent.balance") }}: {{ formatNum(c.balance) }}
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </v-list-tile>
      </v-list>
    </v-card>

    <!-- Selected center detail + recharge -->
    <v-card class="pa-3 mb-3" v-if="selected && selected.center">
      <v-layout row wrap>
        <v-flex xs12 md6 class="px-2">
          <div class="title mb-2">{{ selected.center.name }}</div>
          <div>{{ $t("agent.balance") }}: <strong>{{ formatNum(selected.center.balance) }} SYP</strong>
            <v-chip v-if="selected.low_balance" color="red" text-color="white" small>{{ $t("agent.lowBalance") }}</v-chip>
          </div>
          <div class="grey--text">
            {{ $t("agent.lastRecharge") }}: {{ selected.last_recharge_at ? formatDate(selected.last_recharge_at) : "—" }}
          </div>

          <div class="subheading mt-3">{{ $t("agent.rechargeTitle") }}</div>
          <v-text-field v-model.number="amount" type="number" :label="$t('agent.amount')" />
          <v-text-field v-model="notes" :label="$t('agent.notes')" />
          <div class="grey--text" style="font-size: 12px">
            <v-icon small>place</v-icon>
            {{ gps ? `GPS: ${gps.latitude.toFixed(5)}, ${gps.longitude.toFixed(5)}` : $t("agent.noGps") }}
          </div>
          <v-btn color="primary" class="mt-2" :loading="recharging" :disabled="!amount || amount <= 0" @click="confirmRecharge">
            {{ $t("agent.recharge") }}
          </v-btn>
        </v-flex>

        <v-flex xs12 md6 class="px-2">
          <div class="subheading mb-1">{{ $t("agent.recentRecharges") }}</div>
          <v-data-table
            :headers="rechargeHeaders"
            :items="selected.recent_recharges || []"
            hide-actions
            class="elevation-0 mb-3"
          >
            <template slot="items" slot-scope="props">
              <td>{{ props.item.id }}</td>
              <td>{{ formatNum(props.item.amount) }}</td>
              <td>{{ props.item.performed_by || '—' }}</td>
              <td>{{ formatDate(props.item.created_at) }}</td>
              <td>
                <v-chip v-if="props.item.cancelled_at" color="red" text-color="white" small>{{ $t("agent.cancelled") }}</v-chip>
                <v-chip v-else color="green" text-color="white" small>{{ $t("agent.done") }}</v-chip>
              </td>
            </template>
          </v-data-table>
          <div class="subheading mb-1">{{ $t("agent.recentDriverTransfers") }}</div>
          <v-data-table
            :headers="driverTransferHeaders"
            :items="selected.recent_driver_transfers || []"
            hide-actions
            class="elevation-0"
          >
            <template slot="items" slot-scope="props">
              <td>{{ props.item.driver_name }}</td>
              <td>{{ formatNum(props.item.amount) }}</td>
              <td>{{ formatDate(props.item.created_at) }}</td>
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- My operations log -->
    <v-card class="pa-3">
      <v-layout row align-center class="mb-2">
        <div class="title">{{ $t("agent.myOps") }}</div>
        <v-spacer />
        <v-btn small icon @click="loadMyOps"><v-icon>refresh</v-icon></v-btn>
      </v-layout>
      <v-data-table
        :headers="myOpsHeaders"
        :items="myOps"
        :loading="opsLoading"
        :total-items="opsTotal"
        :pagination.sync="opsPagination"
        class="elevation-0"
      >
        <template slot="items" slot-scope="props">
          <td>{{ props.item.id }}</td>
          <td>{{ props.item.transfer_center_name }}</td>
          <td>{{ formatNum(props.item.amount) }}</td>
          <td>{{ formatDate(props.item.created_at) }}</td>
          <td>
            <v-chip v-if="props.item.cancelled_at" color="red" text-color="white" small>
              {{ $t("agent.cancelled") }}: {{ props.item.cancel_reason }}
            </v-chip>
            <v-btn v-else small flat color="red" @click="promptCancel(props.item)">
              {{ $t("agent.cancel") }}
            </v-btn>
          </td>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "AgentPanel",
  title: "Transfer Agent",
  data() {
    return {
      search: "",
      searching: false,
      centers: [],
      selected: null,
      amount: null,
      notes: "",
      gps: null,
      recharging: false,
      myOps: [],
      opsLoading: false,
      opsTotal: 0,
      opsPagination: { rowsPerPage: 10, page: 1 },
    };
  },
  computed: {
    rechargeHeaders() {
      return [
        { text: "#", value: "id", sortable: false },
        { text: this.$t("agent.amount"), value: "amount", sortable: false },
        { text: this.$t("agent.rechargedBy"), value: "performed_by", sortable: false },
        { text: this.$t("agent.date"), value: "created_at", sortable: false },
        { text: this.$t("agent.status"), value: "status", sortable: false },
      ];
    },
    driverTransferHeaders() {
      return [
        { text: this.$t("agent.driver"), value: "driver_name", sortable: false },
        { text: this.$t("agent.amount"), value: "amount", sortable: false },
        { text: this.$t("agent.date"), value: "created_at", sortable: false },
      ];
    },
    myOpsHeaders() {
      return [
        { text: "#", value: "id", sortable: false },
        { text: this.$t("agent.center"), value: "transfer_center_name", sortable: false },
        { text: this.$t("agent.amount"), value: "amount", sortable: false },
        { text: this.$t("agent.date"), value: "created_at", sortable: false },
        { text: this.$t("agent.status"), value: "status", sortable: false },
      ];
    },
  },
  watch: {
    opsPagination: { handler() { this.loadMyOps(); }, deep: true },
  },
  mounted() {
    this.captureGps();
    this.loadMyOps();
  },
  methods: {
    formatNum(v) { return this.formatMoney(v); },
    // Final-version doc 2026-10-05: company clock (UTC+3), not the PC's zone.
    formatDate(d) { return this.$companyTime(d, "YYYY-MM-DD HH:mm:ss", "—"); },
    captureGps() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => { this.gps = { latitude: pos.coords.latitude, longitude: pos.coords.longitude }; },
          () => { this.gps = null; },
          { enableHighAccuracy: true, timeout: 10000 }
        );
      }
    },
    async doSearch() {
      this.searching = true;
      try {
        const { data } = await this.axios.get("agent/transfer-centers", { params: { search: this.search } });
        this.centers = (data.data && data.data.data) || [];
      } catch (e) {
        window.getApp.$emit("SHOW_TOAST", "Search failed", "red");
      } finally {
        this.searching = false;
      }
    },
    async selectCenter(c) {
      try {
        const { data } = await this.axios.get(`agent/transfer-centers/${c.id}`);
        this.selected = data.data;
      } catch (e) {
        window.getApp.$emit("SHOW_TOAST", "Failed to load center", "red");
      }
    },
    confirmRecharge() {
      const center = this.selected.center;
      this.$dialova({
        title: this.$t("agent.rechargeTitle"),
        paragraph: this.$t("agent.rechargeConfirm", { amount: this.formatNum(this.amount), center: center.name }),
        loader: true,
      })
        .then(async ({ close }) => {
          this.recharging = true;
          try {
            const payload = { amount: this.toStoredAmount(this.amount), notes: this.notes || null };
            if (this.gps) {
              payload.latitude = this.gps.latitude;
              payload.longitude = this.gps.longitude;
            }
            const { data } = await this.axios.post(`agent/transfer-centers/${center.id}/recharge`, payload);
            const res = data.data || {};
            window.getApp.$emit(
              "SHOW_TOAST",
              this.$t("agent.rechargeDone", { center: res.center_name, balance: this.formatNum(res.new_balance) }),
              "green"
            );
            this.amount = null;
            this.notes = "";
            this.selectCenter(center);
            this.loadMyOps();
          } catch (e) {
            window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || "Recharge failed", "red");
          } finally {
            this.recharging = false;
            close();
          }
        })
        .catch(() => {});
    },
    promptCancel(op) {
      const reason = window.prompt(this.$t("agent.cancelReasonPrompt"));
      if (!reason) return;
      this.axios
        .post(`agent/recharges/${op.id}/cancel`, { reason })
        .then(() => {
          window.getApp.$emit("SHOW_TOAST", this.$t("agent.cancelDone"), "green");
          this.loadMyOps();
          if (this.selected && this.selected.center) this.selectCenter(this.selected.center);
        })
        .catch((e) => {
          window.getApp.$emit("SHOW_TOAST", (e.response && e.response.data && e.response.data.message) || "Cancel failed", "red");
        });
    },
    async loadMyOps() {
      this.opsLoading = true;
      try {
        const { data } = await this.axios.get("agent/recharges", {
          params: { perPage: this.opsPagination.rowsPerPage, page: this.opsPagination.page },
        });
        const payload = data.data || {};
        this.myOps = payload.data || [];
        this.opsTotal = payload.total || this.myOps.length;
      } catch (e) {
        window.getApp.$emit("SHOW_TOAST", "Failed to load operations", "red");
      } finally {
        this.opsLoading = false;
      }
    },
  },
};
</script>
